/// <reference types="vite/client" />
/**
 * Shared layout component for all level/subject information pages.
 *
 * What: Provides consistent structure, SEO metadata, and navigation for level pages.
 * Why: Ensures all level pages follow the same pattern and maintain visual/structural consistency.
 * When: Update when changing the overall structure or navigation for level pages.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Container, Stack } from '@mui/material';

import LandingBackgroundAccent from '@/components/landing/LandingBackgroundAccent';
import type { FaqItem } from '@/components/landing/LandingFaqSection';
import LandingFaqSection from '@/components/landing/LandingFaqSection';
import LandingFooter from '@/components/landing/LandingFooter';
import LandingMobileNav from '@/components/landing/LandingMobileNav';
import type { ProgramFactsContent } from '@/components/landing/LandingProgramFactsSection';
import LandingProgramFactsSection from '@/components/landing/LandingProgramFactsSection';
import type { ProofPointsContent } from '@/components/landing/LandingProofSection';
import LandingProofSection from '@/components/landing/LandingProofSection';
import { landingRevealStyles } from '@/components/landing/landingRevealStyles';
import { colors, containerPadding } from '@/components/landing/landingTokens';
import LandingTopBar from '@/components/landing/LandingTopBar';
import ScrollProgressBar from '@/components/landing/ScrollProgressBar';
import type { ChallengesSectionContent } from '@/components/levels/ChallengesSection';
import ChallengesSection from '@/components/levels/ChallengesSection';
import type { HowWeHelpSectionContent } from '@/components/levels/HowWeHelpSection';
import HowWeHelpSection from '@/components/levels/HowWeHelpSection';
import LevelCtaSection from '@/components/levels/LevelCtaSection';
import type { LevelHeroContent } from '@/components/levels/LevelHeroSection';
import LevelHeroSection from '@/components/levels/LevelHeroSection';
import type { StreamSelectorContent } from '@/components/levels/StreamSelectorSection';
import StreamSelectorSection from '@/components/levels/StreamSelectorSection';
import type { SyllabusSectionContent } from '@/components/levels/SyllabusSection';
import SyllabusSection from '@/components/levels/SyllabusSection';
import { getBrandingConfig } from '@/config/runtimeBrandConfig';
import { useAuth } from '@/hooks/useAuth';
import { useSectionFadeIn } from '@/hooks/useSectionFadeIn';
import { getDefaultRedirectPath } from '@/utils/accessControl';
import { trackMetaEvent } from '@/utils/metaPixel.js';

export interface LevelPageLayoutContent {
  id: string;
  slug: string;
  level: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  searchIntentHeading: string;
  intro: string;
  hero: LevelHeroContent;
  streamSelector?: StreamSelectorContent;
  syllabus: SyllabusSectionContent;
  challenges: ChallengesSectionContent;
  approach: HowWeHelpSectionContent;
  programFacts: ProgramFactsContent;
  decisionSupport: ProofPointsContent;
  faqItems: FaqItem[];
}

interface LevelPageLayoutProps {
  content: LevelPageLayoutContent;
  parentBreadcrumbs?: readonly { name: string; path: string }[];
  ogImageUrl?: string;
  children?: React.ReactNode;
  /**
   * Reference content that is the reason the page exists — e.g. the syllabus
   * outline on the two syllabus hubs. Renders directly after the syllabus
   * overview and above every marketing section, because `children` renders
   * below the CTA, which is the wrong place for the content a visitor searched
   * for. Use `children` for anything that genuinely belongs after the CTA.
   */
  referenceContent?: React.ReactNode;
  /**
   * Suppress the default Course JSON-LD. Use on non-level-specific pages
   * (e.g. comparison or buyer's-guide pages) where a Course schema would
   * have a misleading name/description.
   */
  suppressCourseSchema?: boolean;
  /**
   * Force `noindex,nofollow` even in production. Use on a page that is
   * routed and reachable but deliberately withheld from public discovery
   * (e.g. shipped ahead of the content it promises).
   */
  forceNoindex?: boolean;
}

function LevelPageLayout({
  content,
  parentBreadcrumbs = [],
  ogImageUrl,
  children,
  referenceContent,
  suppressCourseSchema = false,
  forceNoindex = false,
}: LevelPageLayoutProps) {
  const { loginWithRedirect } = useAuth0();
  const { isAuthenticated, userRole, isRoleLoaded } = useAuth();
  const navigate = useNavigate();
  const branding = getBrandingConfig();
  const brandName = (branding?.siteTitle as string | undefined) || 'DeepThink';
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : (branding?.siteUrl as string | undefined) || 'https://deepthink.sg';
  const canonicalUrl = `${origin}/${content.slug}`;
  const ogImage =
    (ogImageUrl ? new URL(ogImageUrl, origin).href : undefined) ||
    (branding?.ogImageUrl as string | undefined) ||
    `${origin}/og-image.png`;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Meta Pixel PageView for trial sign-up page attribution (CAPI + Pixel)
  useEffect(() => {
    trackMetaEvent('PageView', {
      content_name: content.metaTitle,
      page_url: canonicalUrl,
    });
  }, [content.metaTitle, canonicalUrl]);

  // Mobile nav drawer state: hamburger lives in top bar for consistent alignment (same as landing page)
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Scroll-triggered reveals for the below-fold sections, same system as the
  // homepage. The hero is excluded so it renders instantly.
  const animatedSectionIds = useMemo(
    () => [
      ...(content.streamSelector ? ['streams'] : []),
      'syllabus',
      'challenges',
      'how-we-help',
      `level-${content.id}-facts`,
      `level-${content.id}-fit`,
      `level-${content.id}-faq`,
      'cta',
    ],
    [content.id, content.streamSelector]
  );
  useSectionFadeIn(animatedSectionIds);

  const handleLogin = () => {
    if (isAuthenticated && isRoleLoaded) {
      navigate(getDefaultRedirectPath(userRole), { replace: true });
    } else {
      loginWithRedirect();
    }
  };

  // Structured data for SEO (use production origin for canonical schema URLs)
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: origin,
      },
      ...parentBreadcrumbs.map((parent, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: parent.name,
        item: `${origin}${parent.path}`,
      })),
      {
        '@type': 'ListItem',
        position: parentBreadcrumbs.length + 2,
        name: content.level,
        item: canonicalUrl,
      },
    ],
  };

  const courseJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: content.metaTitle,
    description: content.intro || content.metaDescription,
    provider: {
      '@type': 'EducationalOrganization',
      name: brandName,
      url: origin,
    },
    educationalLevel: content.level,
    inLanguage: 'en-SG',
    availableLanguage: 'en',
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: 'PT1H30M',
    },
  };

  const faqJsonLd =
    Array.isArray(content.faqItems) && content.faqItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: content.faqItems.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;

  const isProduction = import.meta.env.MODE === 'production';
  const robotsContent =
    isProduction && !forceNoindex
      ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
      : 'noindex,nofollow';

  return (
    <>
      <Helmet>
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={`${brandName} – ${content.level}`}
        />
        <meta property="og:site_name" content={brandName} />
        <meta property="og:locale" content="en_SG" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content.metaTitle} />
        <meta name="twitter:description" content={content.metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta
          name="twitter:image:alt"
          content={`${brandName} – ${content.level}`}
        />
        <meta name="theme-color" content="#F5F7FC" />
        <meta name="robots" content={robotsContent} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
        {!suppressCourseSchema && (
          <script type="application/ld+json">
            {JSON.stringify(courseJsonLd)}
          </script>
        )}
        {faqJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(faqJsonLd)}
          </script>
        )}
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.background,
          position: 'relative',
          overflow: 'hidden',
          // Scroll-reveal animations — see landingRevealStyles.ts for the
          // design system (motion types, intensity tiers, section mapping).
          ...(landingRevealStyles as Record<string, unknown>),
        }}
      >
        <LandingBackgroundAccent />
        <ScrollProgressBar />

        <LandingTopBar
          onLogin={handleLogin}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <LandingMobileNav
          onLogin={handleLogin}
          brandName={brandName}
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />

        <Box
          component="main"
          sx={{
            position: 'relative',
            zIndex: 1,
            pt: { xs: '80px', sm: '88px', md: '96px' },
          }}
        >
          <Container
            maxWidth="lg"
            disableGutters
            sx={{
              px: containerPadding.px,
            }}
          >
            <Stack spacing={{ xs: 6, sm: 8, md: 12 }}>
              <LevelHeroSection
                content={content.hero}
                sourceLocation={`level_${content.id}_hero`}
              />
              {content.streamSelector && (
                <StreamSelectorSection content={content.streamSelector} />
              )}
              <SyllabusSection content={content.syllabus} />
            </Stack>
          </Container>

          {referenceContent}

          <ChallengesSection content={content.challenges} />

          <Container
            maxWidth="lg"
            disableGutters
            sx={{
              px: containerPadding.px,
            }}
          >
            <Stack spacing={{ xs: 6, sm: 8, md: 10 }}>
              <HowWeHelpSection content={content.approach} />
              <LandingProgramFactsSection
                id={`level-${content.id}-facts`}
                content={content.programFacts}
                tone="muted"
                align="center"
              />
              <LandingProofSection
                id={`level-${content.id}-fit`}
                content={content.decisionSupport}
                tone="transparent"
                align="center"
              />
              <LandingFaqSection
                id={`level-${content.id}-faq`}
                content={content.faqItems}
                tone="transparent"
                align="center"
              />
            </Stack>
          </Container>

          <Box sx={{ mt: { xs: 6, sm: 8, md: 10 } }}>
            <LevelCtaSection
              levelId={content.id}
              sourceLocation={`level_${content.id}_cta`}
            />
          </Box>

          {children}

          <LandingFooter />
        </Box>
      </Box>
    </>
  );
}

export default LevelPageLayout;
